import {
  Accordion,
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
  Alert,
  ArrowLeftIcon,
  Badge,
  Box,
  Button,
  Heading,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PlusIcon,
  useToast,
  Spinner,
} from '@razorpay/blade/components';
import gradientGray from 'assets/mss/gradient-gray.svg';
import { SalaryComponent, SalaryComponentCategory } from 'components/SalaryComponents/types';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import React, { useState } from 'react';
import { AddComponentModal, EarningsSection, OthersSection, PreviewCtc } from './components';
import { useSalaryStructure } from './hooks/useSalaryStructure';
import { useSalaryStructureComponents } from './hooks/useSalaryStructureComponents';
import { generateCreateUpdateSalaryStructurePayload } from 'components/SalaryStructure/utils';
import { CustomSalaryStructure, SalaryStructureType } from 'components/SalaryStructure/types';
import { salaryStructureQueries } from '../constants';
import { useQueryClient, useMutation } from 'react-query';
import api from 'api';
import { AppError } from 'utils/AppError';
import { UpdateSalaryStructureGraphQLRequest } from 'api/gql-utils/salaryStructures/types';
import CreateStructureSuccessModal from './components/CreateStructureSuccessModal';
import { SalaryStructuresErrorStateView } from './components/SalaryStructureErrorStateView';
import { validateFormulas } from 'components/EmployeeOnboarding/utils';
type DefineStructureStepProps = {
  isFetchingTemplate: boolean;
  hasTemplateError: boolean;
  refetchTemplate: () => void;
};

const DefineStructureStep = ({
  isFetchingTemplate,
  hasTemplateError,
  refetchTemplate,
}: DefineStructureStepProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const {
    allNonAdhocComponents,
    isLoading: isComponentsLoading,
    refetch,
  } = useSalaryStructureComponents();

  const {
    formData,
    removeComponentById,
    earningComponents,
    variableComponents,
    nonPayableBenefitsComponents,
    salaryStructureComponents,
    updateComponentById,
    addComponentsByIds,
    setEmployerContributions,
    employerContributions,
    removeContributionById,
  } = useSalaryStructure();

  const { setCurrentStep, onSuccess } = useWizardInfo();

  const [isAddComponentModalOpen, setIsAddComponentModalOpen] = useState(false);

  //TODO:  Commented part is for adding contributions to the salary structure which will be picked in m3s2

  // const [isAddContributionModalOpen, setIsAddContributionModalOpen] = useState(false);
  const [error, setError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedComponentIds = salaryStructureComponents.map((comp) => comp.salaryComponent.id);

  //TODO:  Commented part is for adding contributions to the salary structure which will be picked in m3s2

  // const selectedContributionIds = employerContributions.map((comp) => comp.componentId);

  const handleAddComponent = (type: SalaryComponentCategory) => {
    setIsAddComponentModalOpen(true);
  };

  const handleAddComponents = (components: SalaryComponent[]) => {
    addComponentsByIds(
      components.map((component) => component.id),
      components,
    );
  };

  const invalidateSalaryStructureQueries = () => {
    queryClient.invalidateQueries([
      salaryStructureQueries.getSalaryStructure,
      formData.templateId,
      formData.isEditTemplateMode,
    ]);

    queryClient.invalidateQueries([salaryStructureQueries.getSalaryStructuresList]);

    queryClient.refetchQueries({
      queryKey: [salaryStructureQueries.getSalaryStructuresList],
    });
  };

  const createSalaryStructureMutation = useMutation({
    mutationKey: [salaryStructureQueries.createSalaryStructure],
    mutationFn: api.salaryStructures.mutations.createSalaryStructure,
    onSuccess: () => {
      setIsSubmitting(false);
      toast.show({
        content: 'Salary structure created successfully',
        color: 'positive',
      });
      if (!formData?.hasCreatedSalaryStructureBefore) {
        setIsSuccessModalOpen(true);
      } else {
        invalidateSalaryStructureQueries();
        onSuccess?.();
      }
    },
    onError: (e: AppError) => {
      toast.show({
        content: e.message,
        color: 'negative',
      });
      setError(e.message);
      setIsSubmitting(false);
    },
  });

  const updateSalaryStructureMutation = useMutation({
    mutationKey: [salaryStructureQueries.updateSalaryStructure],
    mutationFn: api.salaryStructures.mutations.updateSalaryStructure,
    onSuccess: () => {
      toast.show({
        content: 'Salary structure updated successfully',
        color: 'positive',
      });
      invalidateSalaryStructureQueries();
      setIsSubmitting(false);
      onSuccess?.();
    },
    onError: (e: AppError) => {
      toast.show({
        content: e.message,
        color: 'negative',
      });
      setError(e.message);
      setIsSubmitting(false);
    },
  });

  const handleSubmit = () => {
    const { name, description, isEditTemplateMode, templateId } = formData;
    const errors = validateFormulas(salaryStructureComponents);
    if (errors.length > 0) {
      setError(errors[0]);
      return;
    }

    const salaryStructure: CustomSalaryStructure = {
      name: name as string,
      description,
      type: isEditTemplateMode
        ? (formData.type as SalaryStructureType)
        : ('SALARY_STRUCTURE_TYPE_ORG' as SalaryStructureType),
      ...(isEditTemplateMode ? { id: templateId?.[0] } : {}),
    };
    const payload = generateCreateUpdateSalaryStructurePayload({
      salaryStructure: salaryStructure as CustomSalaryStructure,
      salaryStructureComponents,
      isEditTemplateMode,
    });

    setIsSubmitting(true);

    if (isEditTemplateMode) {
      updateSalaryStructureMutation.mutate(payload as UpdateSalaryStructureGraphQLRequest);
      return;
    } else createSalaryStructureMutation.mutate(payload);
  };

  const handleBack = () => {
    setCurrentStep(0);
  };

  return (
    <>
      <ModalHeader title={formData.name} subtitle={formData.description}></ModalHeader>
      <ModalBody height="100%" padding="spacing.0">
        <Box
          padding="spacing.6"
          display="flex"
          gap="spacing.7"
          height="100%"
          backgroundImage={`url("${gradientGray}")`}
          backgroundRepeat="no-repeat"
          backgroundSize="contain"
          backgroundPosition="right bottom">
          <Box
            width="100%"
            height="100%"
            flex="1"
            display="flex"
            flexDirection="column"
            justifyContent="stretch"
            overflow="auto"
            padding="spacing.1">
            <Box display="flex" justifyContent="space-between">
              <Heading color="surface.text.gray.subtle">Build your structure</Heading>

              <Box display="flex" gap="spacing.4" alignItems="center">
                <Button
                  variant="secondary"
                  onClick={() => handleAddComponent('EARNINGS_CATEGORY')}
                  icon={PlusIcon}
                  isLoading={isComponentsLoading}>
                  Add components
                </Button>

                {/*
                 //TODO:  Commented part is for adding contributions to the salary structure which will be picked in m3s2 
                <Button
                  variant="tertiary"
                  onClick={() => setIsAddContributionModalOpen(true)}
                  icon={PlusIcon}
                  isLoading={isComponentsLoading}>
                  Add contribution
                </Button> */}
              </Box>
            </Box>
            {!!error && (
              <Alert
                description={error}
                marginY="spacing.3"
                color="negative"
                isFullWidth
                isDismissible={false}
                testID="alert-error"
              />
            )}
            <Box marginTop="spacing.5" borderWidth="thinner" borderRadius="medium" overflow="clip">
              {hasTemplateError ? (
                <SalaryStructuresErrorStateView
                  title=""
                  description="This page could not be loaded due to unknown error."
                  retryText="Reload Page"
                  onRetry={refetchTemplate}
                />
              ) : isFetchingTemplate ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="400px">
                  <Spinner accessibilityLabel="loading template" />
                </Box>
              ) : (
                <>
                  <Accordion maxWidth="100%" variant="filled" defaultExpandedIndex={0}>
                    <AccordionItem>
                      <AccordionItemHeader
                        title="Fixed"
                        titleSuffix={
                          <Badge color={earningComponents.length ? 'primary' : 'neutral'}>
                            {earningComponents.length.toString()}
                          </Badge>
                        }
                      />
                      <AccordionItemBody>
                        <EarningsSection
                          components={earningComponents}
                          onDeleteComponent={(id) => removeComponentById(id)}
                          onUpdateComponent={(id, component) => {
                            setError('');
                            updateComponentById(id, component);
                          }}
                        />
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                  <Accordion maxWidth="100%" variant="filled">
                    <AccordionItem>
                      <AccordionItemHeader
                        title="Variable"
                        titleSuffix={
                          <Badge color={variableComponents.length ? 'primary' : 'neutral'}>
                            {variableComponents.length.toString()}
                          </Badge>
                        }
                      />
                      <AccordionItemBody>
                        <EarningsSection
                          components={variableComponents}
                          onDeleteComponent={(id) => removeComponentById(id)}
                          onUpdateComponent={(id, component) => {
                            setError('');
                            updateComponentById(id, component);
                          }}
                        />

                        {!!error && (
                          <Alert
                            description={error}
                            marginY="spacing.3"
                            color="negative"
                            isFullWidth
                            isDismissible={false}
                            testID="alert-error-variable"
                          />
                        )}
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                  <Accordion maxWidth="100%" variant="filled">
                    <AccordionItem>
                      <AccordionItemHeader
                        title="Non-payable benefits"
                        titleSuffix={
                          <Badge
                            color={nonPayableBenefitsComponents.length ? 'primary' : 'neutral'}>
                            {nonPayableBenefitsComponents.length.toString()}
                          </Badge>
                        }
                      />
                      <AccordionItemBody>
                        <OthersSection
                          components={nonPayableBenefitsComponents}
                          contributions={employerContributions}
                          onDeleteComponent={(id) => removeComponentById(id)}
                          onUpdateComponent={(id, component) => {
                            setError('');
                            updateComponentById(id, component);
                          }}
                          onDeleteContribution={(id) => removeContributionById(id)}
                        />
                      </AccordionItemBody>
                    </AccordionItem>
                  </Accordion>
                </>
              )}
            </Box>
          </Box>

          <Box width="calc(max(30%, 400px))" height="100%" overflow="auto">
            <PreviewCtc isPreviewHidden={hasTemplateError} />
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="space-between">
          <Button variant="tertiary" onClick={handleBack} icon={ArrowLeftIcon} iconPosition="left">
            Back: Name & description
          </Button>

          <Button
            variant="primary"
            onClick={handleSubmit}
            isDisabled={isComponentsLoading || isSubmitting || !!error}
            isLoading={isSubmitting}>
            {formData.isEditTemplateMode ? 'Update Structure' : 'Create Structure'}
          </Button>
        </Box>
      </ModalFooter>

      {/* Modals */}
      <AddComponentModal
        isOpen={isAddComponentModalOpen}
        onClose={() => setIsAddComponentModalOpen(false)}
        availableComponents={allNonAdhocComponents}
        selectedComponentIds={selectedComponentIds}
        onAddComponents={handleAddComponents}
        refetchComponents={refetch}
      />

      <CreateStructureSuccessModal
        isOpen={isSuccessModalOpen}
        onDismiss={() => {
          setIsSuccessModalOpen(false);
          invalidateSalaryStructureQueries();
          onSuccess?.();
        }}
      />

      {/* 
      //TODO:  Commented part is for adding contributions to the salary structure which will be picked in m3s2 
      <AddContributionModal
        isOpen={isAddContributionModalOpen}
        onClose={() => setIsAddContributionModalOpen(false)}
        selectedContributionIds={selectedContributionIds}
        onAddContributions={(employerContributions) => {
          setEmployerContributions(employerContributions);
        }}
      /> */}
    </>
  );
};

export default DefineStructureStep;
